/* Cookie notice placement (AJ 2026-10-15, round 3; replaces the 2026-10-14 slim bar and the product page left card).
   The Pandectes app draws its notice inside the open shadow root of <pandectes-cmp>, where theme stylesheets cannot
   reach, so this adds one small stylesheet to that root. AJ's design in the app stays as he set it (box, "Your Privacy
   Matters" title with the cookie icon, centred message, the Privacy Policy and Google's Policy links, Accept and
   Manage Preferences). This file only changes where it sits and, on phones, how tightly it packs:
   - desktop and tablet (750 px and up), product pages included: the app's own bottom right box, untouched, except
     that it sits above any bottom bar the theme shows. No left corner card: the HubSpot chat launcher lives there
     (AJ 2026-10-15 00:56);
   - phones (up to 749 px): a full width sheet along the bottom with rounded top corners. Title and icon on one row,
     the message, the two links on their own row, then Accept and Manage Preferences side by side (44 px tall).
   The text, the links, the buttons and what they do stay Pandectes' (edit them in the app). A Decline or Reject
   button, when the app shows one, gets exactly the same size and fill as Accept. The preferences panel is untouched.
   If Pandectes renames these classes the notice simply shows as configured in the app.
   While the notice shows, this file also keeps the rest of the page usable, through one light-DOM stylesheet:
   - the notice sits above any bottom bar the theme shows (phone buy bar, cart checkout bar, compare tray, mobile
     dock), so Add to cart and Check out are never under it. The bars slide in, so the lift is measured again when
     their slide ends (the 2026-10-14 file measured mid-slide and left the phone buy bar under the notice);
   - the HubSpot chat launcher steps up above the notice only when the notice is in its column (the phone sheet).
     Moved with transform, never hidden; the theme's own bars move it with `translate`, and the two add up;
   - keyboard focus never lands under the notice (WCAG 2.4.11): scroll-padding plus a small focusin check. On product
     pages the check also covers Add to cart while it is the focused control or the next one, so the box never hides
     it as the shopper tabs towards it. A control that scrolling cannot move (inside a sticky or fixed box) moves the
     box to the other bottom corner instead, only until focus goes elsewhere;
   - a spacer after the page lets the footer scroll clear of it.
   Brain record: 30-Targets/Projects/sitewide-design-system-2026-10/working/polish-2026-10-14/round3/answers-2026-10-15-0045/A6-pandectes-new-style.md */
(function () {
  var CSS =
    // Every size: the lift above theme bars (the app's own gap stays), Decline as heavy as Accept, a clear focus ring.
    '.dialog.notice{bottom:calc(var(--actual-float,0px) + var(--cq-consent-lift,0px))}' +
    '.dialog.notice .actions button.deny{background-color:var(--color-primary,#171796);color:var(--color-primary-content,#fff);' +
    'border-color:var(--color-primary,#171796)}' +
    '.dialog.notice :is(button,a):focus-visible{outline:2px solid var(--color-base-content,#0c0c4a);outline-offset:2px}' +
    // Desktop and tablet: the box steps to the other corner while a keyboard-focused pinned control is under it.
    '@media (min-width:750px){.dialog.notice{translate:var(--cq-consent-dx,0px) 0;transition:translate .2s ease}}' +
    '@media (prefers-reduced-motion:reduce){.dialog.notice{transition:none}}' +
    // Phones: a full width sheet that keeps the title, icon and links.
    '@media (max-width:749px){.dialog.notice{--padding:0px;left:0;right:0;top:auto;bottom:var(--cq-consent-lift,0px);' +
    'width:100%;max-width:100%!important;margin:0;border-radius:16px 16px 0 0;' +
    'box-shadow:0 -12px 32px -20px rgba(12,12,74,.45),0 -1px 0 rgba(12,12,74,.08)}' +
    '.dialog.notice .inner{gap:8px;padding:14px 16px calc(14px + env(safe-area-inset-bottom,0px))}' +
    '.dialog.notice .header{padding:0;gap:8px}' +
    '.dialog.notice .header>h2{gap:8px;font-size:16px;line-height:1.25}' +
    '.dialog.notice .header>h2>.logo{flex:0 0 auto;width:24px;height:24px}' +
    '.dialog.notice .body{padding:0;font-size:14px;line-height:1.45}' +
    '.dialog.notice .intro>.links{column-gap:20px;row-gap:0;margin-top:2px}' +
    '.dialog.notice .intro>.links>a{display:inline-flex;align-items:center;min-height:24px;text-underline-offset:2px}' +
    '.dialog.notice .actions{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:4px;padding:0}' +
    '.dialog.notice .actions .group{display:contents}' +
    '.dialog.notice .actions button{flex:none;min-width:0;min-height:44px;margin:0;padding:0 10px;font-size:15px;line-height:1.2}' +
    /* Three buttons on a phone: Accept and Decline share the row, Manage Preferences goes under them as a quiet link. */
    '.dialog.notice .actions:has(.deny) .preferences{grid-column:1/-1;order:2;min-height:40px;border-color:transparent;' +
    'background:none;text-decoration:underline;text-underline-offset:2px}}';

  // Light DOM, only while the notice shows (html.cq-consent-on). --cq-consent-bar is the strip from the notice's top
  // edge down to the theme bar under it, --cq-consent-h the whole covered strip (that plus any theme bar). The tripled
  // class outranks the cart page's own scroll-padding rule so the larger strip wins there. html.cq-consent-chat is set
  // only while the notice is in the chat launcher's column.
  var PAGE_CSS =
    // Not while the box has stepped aside (.cq-consent-dodge): the browser would scroll the page on every Tab
    // through a sticky box without moving the focused control.
    'html.cq-consent-on.cq-consent-on.cq-consent-on:not(.cq-consent-dodge){scroll-padding-bottom:calc(var(--cq-consent-h,0px) + 16px)}' +
    'html.cq-consent-on body::after{content:"";display:block;height:var(--cq-consent-bar,0px)}' +
    'html.cq-consent-on.cq-consent-chat #hubspot-messages-iframe-container{transform:translateY(calc(-1 * var(--cq-consent-bar,0px)))}';

  // Fixed bars the theme pins to the bottom of the screen. The notice sits above whichever of them is showing.
  var BARS = '.sticky-cart.is-visible, [data-cq-page-bar][data-visible], .cq-compare-tray, #mobile-dock, .mobile-dock';
  var LAUNCHER = '#hubspot-messages-iframe-container';
  // The product page buy box and its Add to cart button (the related products' quick add buttons are not in it).
  var BUY_BOX = '.product-info';
  var ADD = 'form[action*="/cart/add"] button[type="submit"], button[name="add"]';
  var STOPS = 'a[href], button, input, select, textarea, [tabindex]';

  var html = document.documentElement;
  var product = document.body.classList.contains('template-product');
  var frame = 0;
  var noticeEl = null;

  function addPageStyle() {
    if (document.getElementById('cq-consent-page')) return;
    var tag = document.createElement('style');
    tag.id = 'cq-consent-page';
    tag.textContent = PAGE_CSS;
    document.head.appendChild(tag);
  }

  function liftHeight() {
    var vh = window.innerHeight;
    var top = vh;
    document.querySelectorAll(BARS).forEach(function (bar) {
      var box = bar.getBoundingClientRect();
      if (box.height && box.width && box.bottom >= vh - 24 && box.top < vh && getComputedStyle(bar).visibility !== 'hidden') {
        top = Math.min(top, box.top);
      }
    });
    return Math.max(0, Math.round(vh - top));
  }

  // The box's own place plus any sideways step (offsetLeft and offsetWidth ignore the dodge translate).
  function noticeSpan() {
    var left = noticeEl.offsetLeft + (parseFloat(html.style.getPropertyValue('--cq-consent-dx')) || 0);
    return { left: left, right: left + noticeEl.offsetWidth };
  }

  // Is the notice in the chat launcher's column? Uses the launcher's frame when HubSpot has drawn it; before that, a
  // notice touching the left 120 px.
  function overChat() {
    var span = noticeSpan();
    var frameEl = document.querySelector(LAUNCHER + ' iframe');
    var chat = frameEl && frameEl.getBoundingClientRect();
    if (chat && chat.width) return span.left < chat.right && span.right > chat.left;
    return span.left < 120;
  }

  // All reads, then the writes, each only when changed.
  function measure() {
    frame = 0;
    var notice = noticeEl && noticeEl.isConnected && noticeEl.hasAttribute('open') ? noticeEl : null;
    var box = notice ? notice.getBoundingClientRect() : null;
    var on = !!(box && box.height > 0);
    var lift = on ? liftHeight() : 0;
    // From the notice's top edge down to the theme bar under it (for a floating box this includes its gap).
    var barHeight = on ? Math.max(0, Math.round(window.innerHeight - box.top - lift)) : 0;
    var chat = on && overChat();
    var moved = on && html.style.getPropertyValue('--cq-consent-lift') !== lift + 'px';
    html.classList.toggle('cq-consent-on', on);
    html.classList.toggle('cq-consent-chat', chat);
    if (on) {
      var values = { '--cq-consent-lift': lift + 'px', '--cq-consent-bar': barHeight + 'px', '--cq-consent-h': (lift + barHeight) + 'px' };
      for (var key in values) if (html.style.getPropertyValue(key) !== values[key]) html.style.setProperty(key, values[key]);
    } else if (html.style.getPropertyValue('--cq-consent-h')) {
      ['--cq-consent-lift', '--cq-consent-bar', '--cq-consent-h', '--cq-consent-dx'].forEach(function (key) { html.style.removeProperty(key); });
      html.classList.remove('cq-consent-dodge');
    }
    // A theme bar came or went under the notice after a keyboard focus scroll (the phone buy bar slides in once the
    // buy box scrolls away): check the focused control again against the notice's new place.
    var active = document.activeElement;
    if (moved && active && active !== document.body && active.matches && active.matches(':focus-visible') &&
        !(active.closest && active.closest('pandectes-cmp'))) {
      requestAnimationFrame(function () { clearFocus(active); });
    }
  }
  function schedule() { if (!frame) frame = requestAnimationFrame(measure); }
  // Once now and once after the theme bars' slide (about 160 ms) has had time to finish.
  function scheduleTwice() { schedule(); setTimeout(schedule, 320); }

  var resize = window.ResizeObserver ? new ResizeObserver(schedule) : null;

  function watch(root) {
    var found = root.querySelector('.dialog.notice');
    if (found !== noticeEl) {
      if (resize && noticeEl) resize.unobserve(noticeEl);
      noticeEl = found;
      if (resize && noticeEl) resize.observe(noticeEl);
    }
    schedule();
  }

  function style(host) {
    var root = host.shadowRoot;
    if (!root || root.__cqConsent) return;
    root.__cqConsent = true;
    try {
      var sheet = new CSSStyleSheet();
      sheet.replaceSync(CSS);
      root.adoptedStyleSheets = root.adoptedStyleSheets.concat([sheet]);
    } catch (e) {
      var tag = document.createElement('style');
      tag.textContent = CSS;
      root.appendChild(tag);
    }
    addPageStyle();
    watch(root);
    // Pandectes adds and removes its dialogs inside the root; follow the notice as it comes and goes.
    new MutationObserver(function () { watch(root); })
      .observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'class'] });
  }

  function scan() { document.querySelectorAll('pandectes-cmp').forEach(style); }

  // A control focused from the keyboard is scrolled clear of the notice when scroll-padding alone did not
  // (WCAG 2.4.11). Checked after the browser's own focus scroll, and once more after a theme bar under the notice
  // (the phone buy bar) has had time to appear or go, since that moves the notice.
  function pinned(el) {
    for (var e = el; e && e !== document.body; e = e.parentElement) {
      var pos = getComputedStyle(e).position;
      if (pos === 'sticky' || pos === 'fixed') return true;
    }
    return false;
  }
  function dodge(px) {
    var value = px ? px + 'px' : '';
    if (html.style.getPropertyValue('--cq-consent-dx') === value) return;
    if (value) html.style.setProperty('--cq-consent-dx', value); else html.style.removeProperty('--cq-consent-dx');
    html.classList.toggle('cq-consent-dodge', !!px);
    schedule();
  }
  // Product pages: the Add to cart button when the focused control is it or the tab stop right before it (an
  // unchecked radio is not a stop when its group has a checked one).
  function addToCart(target) {
    if (!product || !target.closest) return null;
    var scope = target.closest(BUY_BOX), add = null;
    // The first one drawn: a subscription app can keep a hidden original next to the visible copy.
    if (scope) [].some.call(scope.querySelectorAll(ADD), function (el) { return el.getClientRects().length && (add = el); });
    if (!add) return null;
    if (target === add) return add;
    var stops = [].filter.call(scope.querySelectorAll(STOPS), function (el) {
      if (el.disabled || el.tabIndex < 0 || el.type === 'hidden' || !el.getClientRects().length ||
          (el.closest && el.closest('[inert]')) || getComputedStyle(el).visibility === 'hidden') return false;
      if (el.type === 'radio' && !el.checked && el.name && window.CSS && window.CSS.escape &&
          scope.querySelector('input[type="radio"][name="' + window.CSS.escape(el.name) + '"]:checked')) return false;
      return true;
    });
    var i = stops.indexOf(target);
    return i > -1 && stops[i + 1] === add ? add : null;
  }
  function clearFocus(target) {
    if (!html.classList.contains('cq-consent-on') || !noticeEl || document.activeElement !== target) return;
    var r = target.getBoundingClientRect();
    var box = { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
    var edge = noticeEl.getBoundingClientRect().top;
    // Product pages: Add to cart joins the check while it is the focused control or the next one. When the two
    // together are too tall to scroll clear of the notice without pushing the focused control under the header,
    // the focused control alone is scrolled and a corner box steps aside if Add to cart is under it.
    var add = addToCart(target), addBox = null, tall = false;
    if (add && add !== target) {
      var a = add.getBoundingClientRect();
      addBox = { top: a.top, bottom: a.bottom, left: a.left, right: a.right };
      var both = { top: Math.min(r.top, a.top), bottom: Math.max(r.bottom, a.bottom), left: Math.min(r.left, a.left), right: Math.max(r.right, a.right) };
      if (both.bottom - both.top <= edge - 120) box = both; else tall = true;
    }
    // The notice's own place, without the sideways step (offsetLeft and offsetWidth ignore translate).
    var left = noticeEl.offsetLeft, width = noticeEl.offsetWidth, room = html.clientWidth;
    function hit(b) { return b.bottom > edge && b.top < window.innerHeight && b.right > left && b.left < left + width; }
    var under = hit(box);
    // A corner box (not the full width phone sheet) can step to the mirror corner: its left edge moves to where its
    // right gap is now (the used `right` value), so the bottom right box moves left by the difference.
    var corner = width < room - 40;
    var stuck = pinned(target) || (!!add && !tall && pinned(add));
    if (corner && target.matches(':focus-visible') && ((under && stuck) || (tall && hit(addBox)))) {
      dodge(Math.round((parseFloat(getComputedStyle(noticeEl).right) || 0) - left));
      return;
    }
    dodge(0);
    if (under && box.bottom - box.top < edge) {
      window.scrollBy({ top: box.bottom - edge + 16, behavior: 'instant' });
    }
  }
  document.addEventListener('focusin', function (event) {
    var target = event.target;
    if (!html.classList.contains('cq-consent-on') || !target || !target.getBoundingClientRect || target === document.body ||
        (target.closest && target.closest('pandectes-cmp'))) return;
    requestAnimationFrame(function () { clearFocus(target); setTimeout(function () { clearFocus(target); }, 200); });
  });

  if (!window.customElements) return;
  customElements.whenDefined('pandectes-cmp').then(function () {
    scan();
    // New children of body: Pandectes' host, and HubSpot's launcher (decides whether the chat steps up).
    new MutationObserver(function () { scan(); schedule(); }).observe(document.body, { childList: true });
    // The theme's bottom bars announce themselves with body classes (cq-buybar-on, cq-cartbar-on, cq-compare-bar-on)
    // and then slide in, so measure again when the slide ends.
    new MutationObserver(scheduleTwice).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    ['transitionend', 'animationend'].forEach(function (type) {
      document.addEventListener(type, function (event) {
        var el = event.target;
        if (html.classList.contains('cq-consent-on') && el && el.matches && el.matches(BARS)) schedule();
      }, true);
    });
    window.addEventListener('resize', schedule, { passive: true });
  });
})();
